import { useIntl } from "react-intl";
import Radio from "components/atoms/Radio/Radio";
import { useFormContext } from "react-hook-form";
import testIds from "constants/testIds";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { pickupDeliveryOption } from "../ScheduleDefaultOptions";

interface AddressTypeSelectorProps {
  partyType: string;
  disabled: boolean;
}

export default function AddressTypeSelector({ partyType, disabled }: AddressTypeSelectorProps) {
  const intl = useIntl();
  const { getValues, setValue, trigger } = useFormContext();
  const isPortAddressV2Enabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);

  // When the WEB-10382 port-linked address flow is active, clear the party's
  // previously selected address + reference_number when the user switches
  // between address / airport / seaport. Values are repopulated from the next
  // port selection (auto-fill) or a manual address choice.
  const clearPartyAddressFields = () => {
    setValue(`schedule.${partyType}.address_reference_number`, "");
    setValue(`schedule.${partyType}.address_name`, "");
    setValue(`schedule.${partyType}.address_street`, "");
    setValue(`schedule.${partyType}.address_street_line2`, "");
    setValue(`schedule.${partyType}.address_city`, "");
    setValue(`schedule.${partyType}.address_state`, "");
    setValue(`schedule.${partyType}.address_country`, "");
    setValue(`schedule.${partyType}.address_zipcode`, "");
  };

  const handleChangeAddressType = (event: any) => {
    const nextAddressType = event?.target?.value;

    // Radio.customOnChange runs before RHF writes the next value.
    // Rely on event.target.value to avoid stale getValues() reads.
    if (!nextAddressType) return;

    setValue(`schedule.${partyType}.airport`, "");
    setValue(`schedule.${partyType}.airport_name`, "");
    setValue(`schedule.${partyType}.seaport`, "");
    setValue(`schedule.${partyType}.seaport_name`, "");

    // Keep the address picker in sync across type switches.
    setValue(`schedule.${partyType}.address_name`, "");

    if (isPortAddressV2Enabled) {
      clearPartyAddressFields();
    }
    trigger(`schedule.${partyType}`);
  };

  return (
    <div
      data-testid={
        partyType === "notify_party"
          ? testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_ADDRESS_TYPE
          : testIds.REQUEST_FORM_SCHEDULE_CONSIGNEE_ADDRESS_TYPE
      }
    >
      <Radio
        className="border-0 d-flex flex-row width-fit p-none mb-xl"
        customOption
        name={`schedule.${partyType}.address_type`}
        defaultChecked={getValues(`schedule.${partyType}.address_type` || "address")}
        options={pickupDeliveryOption(intl)}
        customColClass="pl-0"
        customOnChange={handleChangeAddressType}
        disabled={disabled}
        testIdPrefix={
          partyType === "notify_party"
            ? testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_ADDRESS_TYPE
            : testIds.REQUEST_FORM_SCHEDULE_CONSIGNEE_ADDRESS_TYPE
        }
      />
    </div>
  );
}
